import { Select } from 'antd';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  type AtlasGqlFilterBy,
  AtlasGqlFilterOperator,
  useGetWorkCampaignsChooserLazyQuery,
  useGetWorkOrdersChooserLazyQuery,
} from 'types/atlas-graphql';
import { type WORK_ORDER, TASK_STATUS_LABELS, TASK_STATUSES, WORK_CAMPAIGN } from 'utils/constants';

import type { Container } from './types';

const STATUSES = [
  { status: TASK_STATUSES.PENDING, label: TASK_STATUS_LABELS.PENDING },
  { status: TASK_STATUSES.IN_PROGRESS, label: TASK_STATUS_LABELS.IN_PROGRESS },
  { status: TASK_STATUSES.COMPLETE, label: TASK_STATUS_LABELS.COMPLETE },
];

const getOptions = ({ status, label, containerList = [], containerId }: any) => ({
  label,
  options: containerList
    .filter((container: Container) => container.status === status)
    .map((container: Container) => ({
      value: container.id,
      disabled: container.id === containerId,
      label: `${container.number} ${container.name ? ` - ${container.name}` : ''}`,
    })),
});

interface IWorkContainerChooserProps {
  containerId: any;
  type: typeof WORK_CAMPAIGN | typeof WORK_ORDER;
  allowClear?: boolean;
  id?: any;
  labelInValue?: boolean;
  onChange?: any;
  disabled?: boolean;
  testId?: string;
  // Keep below to continue working with our js files
  props?: any;
}

export const WorkContainerChooser: React.FunctionComponent<IWorkContainerChooserProps> = ({
  containerId,
  type,
  allowClear,
  id,
  onChange,
  disabled,
  testId,
}) => {
  const { t } = useTranslation();
  const placeholder =
    type === WORK_CAMPAIGN
      ? t('workOrder.selectCampaign', 'Select Campaign')
      : t('workOrder.selectWorkOrder', 'Select Work Order');

  const [loading, setLoading] = useState<true | false>();
  const [containerList, setContainerList] = useState<any[] | null>([]);

  const [getWorkCampaigns, { loading: workCampaignsLoading, data: workCampaignsData }] =
    useGetWorkCampaignsChooserLazyQuery({ fetchPolicy: 'no-cache' });
  const [getWorkOrders, { loading: workOrdersLoading, data: workOrdersData }] =
    useGetWorkOrdersChooserLazyQuery({ fetchPolicy: 'no-cache' });

  useEffect(() => {
    // Users should not be able to modify archived work containers in any way
    // So query for only non-archived work containers to populate the dropdown
    const filters: AtlasGqlFilterBy[] = [
      {
        key: 'archived',
        values: [],
        operator: AtlasGqlFilterOperator.IsNull,
      },
    ];
    if (type === WORK_CAMPAIGN) {
      getWorkCampaigns({ variables: { filterBy: filters } });
    } else {
      getWorkOrders({ variables: { filterBy: filters } });
    }
  }, []);

  useEffect(() => {
    if (workCampaignsData && !workCampaignsLoading) {
      const listData = workCampaignsData?.paginatedWorkCampaigns?.items ?? [];
      setContainerList(listData);
      setLoading(workCampaignsLoading);
    }
  }, [workCampaignsLoading, workCampaignsData]);

  useEffect(() => {
    if (workOrdersData && !workOrdersLoading) {
      const listData = workOrdersData?.paginatedWorkOrders?.items ?? [];
      setContainerList(listData);
      setLoading(workOrdersLoading);
    }
  }, [workOrdersLoading, workOrdersData]);

  return (
    <Select
      allowClear={allowClear}
      data-testid={testId}
      id={id}
      loading={loading}
      filterOption={(input, option) =>
        !!option &&
        !('options' in option) &&
        option.label.toUpperCase().includes(input.toUpperCase())
      }
      onChange={onChange}
      placeholder={placeholder}
      showSearch
      style={{ minWidth: '15rem' }}
      popupMatchSelectWidth={false}
      disabled={disabled}
      options={
        loading
          ? []
          : [
              // null on this option will allow the user to deselect a campaign
              {
                key: 'no-work-container',
                value: null,
                disabled: containerId === null,
                label: t('components.none', 'None'),
              },
              ...STATUSES.map((status) => getOptions({ ...status, containerList, containerId })),
            ]
      }
    />
  );
};
